import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { toAsset } from '../utils/url';

interface CardShuffleAnimationProps {
  isShuffling: boolean;
  onShuffleComplete: () => void;
  duration?: number;
}

const CARD_COUNT = 9;
const getShuffleDims = () => {
  const mobile = typeof window !== 'undefined' && window.innerWidth < 640;
  return mobile
    ? { cardW: 80, cardH: 120, containerW: Math.min((window.innerWidth || 375) - 32, 320), containerH: 260, spreadX: 160, spreadY: 110 }
    : { cardW: 120, cardH: 180, containerW: 600, containerH: 420, spreadX: 340, spreadY: 220 };
};

interface CardAnim {
  x1: number; y1: number;
  x2: number; y2: number;
  r1: number; r2: number;
  s1: number; s2: number;
  delay: number;
  zBase: number;
}

interface Sparkle {
  leftPct: number;
  topPct: number;
  size: number;
  delay: number;
  dur: number;
}

const CardShuffleAnimation: React.FC<CardShuffleAnimationProps> = ({
  isShuffling,
  onShuffleComplete,
  duration = 3000,
}) => {
  const [cards, setCards] = useState<CardAnim[]>([]);
  const [sparkles, setSparkles] = useState<Sparkle[]>([]);
  const [dims] = useState(getShuffleDims);
  const { theme } = useTheme();
  const cardBackUrl = theme.images?.cardBackImageUrl
    ? toAsset(theme.images.cardBackImageUrl)
    : undefined;

  useEffect(() => {
    if (!isShuffling) return;

    const newCards: CardAnim[] = Array.from({ length: CARD_COUNT }, (_, i) => ({
      x1: (Math.random() - 0.5) * dims.spreadX * 2,
      y1: (Math.random() - 0.5) * dims.spreadY * 2,
      x2: (Math.random() - 0.5) * dims.spreadX * 2,
      y2: (Math.random() - 0.5) * dims.spreadY * 2,
      r1: (Math.random() - 0.5) * 90,
      r2: (Math.random() - 0.5) * 90,
      s1: 0.82 + Math.random() * 0.25,
      s2: 0.9 + Math.random() * 0.25,
      delay: i * 0.09,
      zBase: i,
    }));

    const newSparkles: Sparkle[] = Array.from({ length: 14 }, () => ({
      leftPct: 10 + Math.random() * 80,
      topPct: 10 + Math.random() * 80,
      size: 10 + Math.random() * 14,
      delay: Math.random() * 2.5,
      dur: 1.2 + Math.random() * 1.2,
    }));

    setCards(newCards);
    setSparkles(newSparkles);

    const timer = setTimeout(onShuffleComplete, duration);
    return () => clearTimeout(timer);
  }, [isShuffling]);

  if (!isShuffling || cards.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
      <h3 className="text-lg sm:text-3xl font-mystical font-bold text-white mb-6 sm:mb-10">
        Embaralhando as Cartas...
      </h3>

      {/* Área da animação */}
      <div
        className="relative mx-auto mb-6 sm:mb-10"
        style={{ width: dims.containerW, height: dims.containerH }}
      >
        {/* Sparkles flutuantes */}
        {sparkles.map((sp, i) => (
          <motion.div
            key={`sp-${i}`}
            style={{
              position: 'absolute',
              left: `${sp.leftPct}%`,
              top: `${sp.topPct}%`,
              fontSize: sp.size,
              pointerEvents: 'none',
              zIndex: 30,
            }}
            animate={{ opacity: [0, 1, 0], scale: [0.4, 1.3, 0.4], y: [0, -50] }}
            transition={{ duration: sp.dur, repeat: Infinity, delay: sp.delay, ease: 'easeOut' }}
          >
            ✨
          </motion.div>
        ))}

        {/* Cartas */}
        {cards.map((c, i) => (
          <motion.div
            key={`card-${i}`}
            style={{
              position: 'absolute',
              width: dims.cardW,
              height: dims.cardH,
              left: '50%',
              top: '50%',
              marginLeft: -dims.cardW / 2 + i * 3,
              marginTop: -dims.cardH / 2 + i * 2,
              borderRadius: 10,
              overflow: 'hidden',
              zIndex: c.zBase,
            }}
            animate={{
              x: [0, c.x1, c.x2, 0],
              y: [0, c.y1, c.y2, 0],
              rotate: [0, c.r1, c.r2, 0],
              scale: [1, c.s1, c.s2, 1],
            }}
            transition={{
              duration: duration / 1000,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: c.delay,
            }}
          >
            {cardBackUrl ? (
              <img
                src={cardBackUrl}
                alt="Carta"
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.12)', transformOrigin: 'center' }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, #3b1f7c, #1e1b4b)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 36,
                }}
              >
                🔮
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <div className="flex items-center gap-3 text-white">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          className="w-6 h-6 border-2 border-gold-400 border-t-transparent rounded-full"
        />
        <span className="text-sm sm:text-lg font-elegant">Conectando com as energias...</span>
      </div>
    </div>
  );
};

export default CardShuffleAnimation;
